import { FC, Fragment, useEffect } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { useTheme } from '@emotion/react';
import { Divider, Stack, Typography, IconButton, Box } from '@mui/material';

import { getTheme } from 'src/utils/get-theme';
import { LanguagesCode } from 'src/i18n/constants';
import { BlockWithIcon } from 'src/components/block-with-icon';
import { routes } from 'src/constants/routes';
import { useFeatureFlags, useGetCurrentUser, UserAvatar } from 'src/features/user';
import { BookingsOverview, useIsCheckedIn, useGetActiveBooking } from 'src/features/bookings';
import { ReactComponent as IconArrow } from 'src/assets/svg/control-arrow.svg';
import { ReactComponent as QuestionIcon } from 'src/assets/svg/question-in-circle.svg';
import { ReactComponent as FeedbackIcon } from 'src/assets/svg/chat-with-star.svg';
import { ReactComponent as ChatIcon } from 'src/assets/svg/chat.svg';
import { ReactComponent as VideoIcon } from 'src/assets/svg/video.svg';
import { PromotionCards } from 'src/features/promotions';
import { MoveInCard, MoveOutCard } from 'src/features/move-in';
import { appConfig } from 'src/app/app-config';
import { RebookingBanner } from 'src/features/rebooking_27-28';
import { GA4 } from 'src/lib/ga4';
import { Title } from 'src/utils/enum/Title';
import { useGetVoucherCode } from 'src/features/bookings/hooks/use-get-voucher-code';
import { CopyButton } from 'src/components/copy-button';

import {
    MidYearRebookersPromotionList,
    MidYearRebookersReferralsPromotionList,
} from '../../features/promotions/api';
import { PromotionCard } from '../../features/promotions/components/promotion-cards/parts/promotion-card';
import { ReferralCard } from '../../features/promotions/components/promotion-cards/referral-card';
import { useGetZendeskSsoUrl } from '../../features/support-hub/hooks/useGetZendeskSsoUrl';
import { useContactModalStore } from '../../features/rebooking/store';
import { RebookingChoicesModal } from '../../features/rebooking-choices-modal';
import { useGetRebookingInfo } from '../../features/rebooking/hooks/use-get-rebooking-info';
import { RebookingChoicesCard } from '../../features/rebooking-choices-card';

import { ReactComponent as ChairIcon } from './parts/chair.svg';

export const HomePage: FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const contactUsSearchParam = 'contact_us';

    const { breakpoints, palette } = useTheme();
    const { isHosTheme } = getTheme();
    const { t } = useTranslation();
    const { openModal } = useContactModalStore.getState();
    const shouldOpenModal = searchParams.get(contactUsSearchParam) === 'true';

    const { data: activeBooking } = useGetActiveBooking();
    const { mutate: getZendeskSsoUrl, error: zendeskError } = useGetZendeskSsoUrl();
    const { data: voucherCode } = useGetVoucherCode();

    const featureFlags = useFeatureFlags();
    const isCheckedIn = useIsCheckedIn();

    const { data: rebookingInfo } = useGetRebookingInfo();
    const { data: user } = useGetCurrentUser();

    const rebookingIsActive = rebookingInfo?.isActive;

    const isNominationUser =
        !featureFlags.move_in &&
        !featureFlags.move_out &&
        !featureFlags.booking &&
        !featureFlags.make_payment;

    const handleClick = () =>
        GA4.ctaEvent({
            name: 'View Profile',
            label: t('profile_update', { lng: LanguagesCode.English }),
            location: 'Home Page',
        });

    const getTitleKey = () =>
        isNominationUser && user?.first_name
            ? 'booking_overview_title_nominations'
            : 'booking_overview_title';

    const brand = isHosTheme ? Title.HOUSE_OF_SOCIAL : Title.VITA_STUDENT;

    const midYearRebookerPromotions = MidYearRebookersPromotionList.filter((promotion) =>
        isHosTheme ? promotion.show_on_hos : promotion.show_on_vs
    );

    const midYearRebookerReferralPromotions = MidYearRebookersReferralsPromotionList.filter(
        (promotion) => (isHosTheme ? promotion.show_on_hos : promotion.show_on_vs)
    );

    useEffect(() => {
        if (shouldOpenModal) {
            openModal();
            setSearchParams(
                (currentSearchParams) => {
                    currentSearchParams.set(contactUsSearchParam, '');
                    currentSearchParams.delete(contactUsSearchParam);
                    return currentSearchParams;
                },
                { replace: true }
            );
        }
    }, [searchParams]);

    if (zendeskError) {
        return <Navigate to={routes.notFound} />;
    }

    if (shouldOpenModal) {
        openModal();
        setSearchParams(
            (currentSearchParams) => {
                currentSearchParams.set(contactUsSearchParam, '');
                return currentSearchParams;
            },
            { replace: true }
        );
    }

    return (
        <Fragment>
            {activeBooking?.development_slug && (
                <RebookingChoicesModal developmentSlug={activeBooking.development_slug} />
            )}
            <Box padding="36px 16px 16px 16px" display={{ md: 'none' }}>
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    marginBottom="16px"
                    onClick={handleClick}
                    css={{ textDecoration: 'none' }}
                >
                    <UserAvatar onClick={handleClick} />

                    <IconButton onClick={handleClick} component={Link} to={routes.profile.index}>
                        <IconArrow
                            css={{
                                transform: 'rotate(270deg)',
                                stroke: palette.text.primary,
                            }}
                        />
                    </IconButton>
                </Stack>
                <Divider />
            </Box>

            <Stack
                direction={{ xs: 'column', lg: 'row' }}
                gap={{ xs: '48px', lg: '0' }}
                flex={1}
                css={{
                    [breakpoints.down('md')]: {
                        background: palette.background.paper,
                    },
                }}
            >
                {featureFlags.rebook && rebookingIsActive && (
                    <Fragment>
                        <RebookingBanner
                            css={{
                                marginY: '8px',
                                borderBottom: 'none',

                                [breakpoints.up('md')]: {
                                    display: 'none',
                                },
                            }}
                            rebookStatus={rebookingInfo.rebookStatus}
                        />
                        <Box
                            css={{
                                [breakpoints.up('md')]: {
                                    display: 'none',
                                },
                            }}
                        >
                            {activeBooking?.development_slug && (
                                <RebookingChoicesCard
                                    developmentSlug={activeBooking.development_slug}
                                />
                            )}
                        </Box>
                    </Fragment>
                )}

                <Stack
                    gap="20px"
                    css={{
                        flex: 1,
                        padding: '24px 16px',
                        background: palette.background.paper,

                        [breakpoints.up('lg')]: {
                            maxWidth: '560px',
                            padding: '120px 60px 60px 60px',
                        },
                    }}
                >
                    <Typography variant="h2" marginBottom={{ xs: '12px', md: '32px' }}>
                        <Trans
                            i18nKey={getTitleKey()}
                            values={{ firstName: user?.first_name?.toLocaleUpperCase() }}
                        />
                    </Typography>

                    {voucherCode && (
                        <Box
                            maxWidth={{ md: '640px' }}
                            textAlign="left"
                            css={{
                                width: '100%',
                                background: palette.common.white,
                                boxShadow: '2px 4px 30px rgba(0, 0, 0, 0.08)',
                            }}
                        >
                            <Typography
                                variant="body1"
                                color={palette.text.primary}
                                padding="16px"
                                display="flex"
                                alignItems="center"
                                justifyContent="flex-start"
                                gap={1}
                            >
                                {t('wifi_voucher_code')}
                                <strong>{voucherCode}</strong>
                                <CopyButton text={voucherCode} />
                            </Typography>
                        </Box>
                    )}
                    {featureFlags.move_in && <MoveInCard />}

                    {featureFlags.move_out && <MoveOutCard />}

                    {featureFlags.make_payment && <BookingsOverview />}

                    <Stack gap="16px">
                        <BlockWithIcon
                            icon={<QuestionIcon />}
                            title={t('help_page_link_title').toLocaleUpperCase()}
                            body={t('help_page_link_subtitle')}
                            {...(appConfig.zendeskSsoEnabled
                                ? {
                                      onClick: () => getZendeskSsoUrl(),
                                  }
                                : {
                                      link: routes.supportHub.index,
                                  })}
                        />

                        <Divider />

                        {appConfig.zendeskSsoEnabled && (
                            <Fragment>
                                <BlockWithIcon
                                    icon={<ChatIcon />}
                                    title={t('sidebar_navigation_contact')}
                                    body={t('sidebar_navigation_contact_cta')}
                                    onClick={openModal}
                                />

                                <Divider />
                            </Fragment>
                        )}

                        {featureFlags.booking && (
                            <BlockWithIcon
                                icon={<ChairIcon />}
                                title={t('page_link_title').toLocaleUpperCase()}
                                body={t('my_bookings_page_link_subtitle')}
                                link={routes.myBooking.index}
                            />
                        )}

                        <Divider />

                        {featureFlags.feedback && isCheckedIn && (
                            <Fragment>
                                <BlockWithIcon
                                    icon={<FeedbackIcon />}
                                    title={t('my_account_feedback_reviews_title')}
                                    body={t('my_account_feedback_reviews_subtitle')}
                                    link={routes.feedback.index}
                                />

                                <Divider />
                            </Fragment>
                        )}

                        {featureFlags.building_videos && (
                            <BlockWithIcon
                                icon={<VideoIcon />}
                                title={t('home_video-guides-link_title')}
                                body={t('home_video-guides-link_description', { brand })}
                                link={routes.guides.index}
                            />
                        )}
                    </Stack>
                </Stack>

                <Box
                    padding={{ xs: '0 16px', md: '0 16px 32px 16px', lg: '120px 60px 60px 60px' }}
                    flex={1}
                    css={{
                        [breakpoints.up('lg')]: {
                            background: palette.background.default,
                        },
                    }}
                >
                    <Typography variant="h2" marginBottom={{ xs: '32px', md: '52px' }}>
                        {t('exclusive_offers_title').toLocaleUpperCase()}
                    </Typography>

                    <Box
                        display="grid"
                        gridTemplateColumns={{ xs: '1fr', xl: '1fr 1fr' }}
                        gap="32px"
                    >
                        {featureFlags.rebook && rebookingIsActive && (
                            <Fragment>
                                <RebookingBanner
                                    css={{
                                        [breakpoints.down('md')]: {
                                            display: !rebookingIsActive ? undefined : 'none',
                                            margin: '0 -16px',
                                        },
                                    }}
                                    rebookStatus={rebookingInfo.rebookStatus}
                                />
                                <Box
                                    css={{
                                        [breakpoints.down('md')]: {
                                            display: !rebookingIsActive ? undefined : 'none',
                                        },
                                    }}
                                >
                                    {activeBooking?.development_slug && (
                                        <RebookingChoicesCard
                                            developmentSlug={activeBooking.development_slug}
                                        />
                                    )}
                                </Box>
                            </Fragment>
                        )}

                        {featureFlags.rebook &&
                            rebookingIsActive &&
                            !isNominationUser &&
                            !activeBooking?.is_affordable_room &&
                            midYearRebookerPromotions.map((promotion) => (
                                <PromotionCard promotion={promotion} key={promotion.offer_id} />
                            ))}

                        {!isNominationUser &&
                            activeBooking &&
                            !activeBooking.is_affordable_room && (
                                <Fragment>
                                    {midYearRebookerReferralPromotions.map((promotion) => (
                                        <PromotionCard
                                            promotion={promotion}
                                            key={promotion.offer_id}
                                        />
                                    ))}
                                    <ReferralCard />
                                </Fragment>
                            )}

                        <PromotionCards />
                    </Box>
                </Box>
            </Stack>
        </Fragment>
    );
};
